import * as React from 'react';

import { MarkLoadAsReceivedPopover } from '@rematter/modules/customer-portal/_components/_shared/MarkLoadAsReceived';
import {
  DataGrid,
  DataGridSelection,
  FloatingToolbar,
  FontSemiBold,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { LoadRecord } from '../../_graphql/queryLoadList';
import * as state from '../../_state/loadList.state';
import { customerPortalRoutes } from '../../routes';
import { canLoadBeMarkedAsReceived } from '../../utils/loadUtils';
import { loadDocuments } from '../_shared/availableDocuments';
import {
  DownloadDocumentsButton,
  FetchDocumentsPopover,
  PrintDocumentsButton,
} from '../_shared/FetchDocuments';
import { MarkLoadAsReceivedButton } from '../_shared/MarkLoadAsReceived';
import { MarkLoadAsReviewedPopover } from '../_shared/MarkLoadAsReviewed';

import { useLoadListColumns } from './hooks/useLoadListColumns';
import { LoadListFilterBar } from './LoadListFilterBar';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import { useMultiRef } from '~/shared/hooks/useMultiRef';

//
// ⚛️ ⚛️ ⚛️ LOAD LIST VIEW ⚛️ ⚛️ ⚛️
//
export const LoadListView = () => {
  const [, navigate] = useModuleLocation();

  // States
  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );
  const [loadIdForPrint, setLoadIdForPrint] = React.useState<string>();
  const [loadIdForDownload, setLoadIdForDownload] = React.useState<string>();
  const [loadIdForReceiving, setLoadIdForReceiving] = React.useState<string>();
  const [loadIdForReviewing, setLoadIdForReviewing] = React.useState<string>();

  // Atoms
  const [limit = 50, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);

  const { data, isLoading, isError, error, loadingState } = useAtomValue(
    state.$remoteLoads,
  );

  const menuRefs = useMultiRef<HTMLButtonElement>();

  // Callbacks
  const handleCellClick = React.useCallback(
    (row: LoadRecord) => {
      navigate(customerPortalRoutes.loadDetails, {
        params: { loadId: row.id },
      });
    },
    [navigate],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setOffset(newOffset);
      setLimit(newLimit);
    },
    [setOffset, setLimit],
  );

  const columns = useLoadListColumns({
    onRowClick: handleCellClick,
    menuRefs,
    setLoadIdForPrint,
    setLoadIdForDownload,
    setLoadIdForReceiving,
    setLoadIdForReviewing,
  });

  // Memoized values
  const loads = React.useMemo(() => data?.loads ?? [], [data?.loads]);
  const total = React.useMemo(() => data?.total ?? 0, [data?.total]);
  const selectedLoads = React.useMemo(
    () => loads.filter((it) => selection === 'all' || selection.has(it.id)),
    [loads, selection],
  );
  const canMarkSelectedAsReceived = React.useMemo(
    () => selectedLoads.every((it) => canLoadBeMarkedAsReceived(it)),
    [selectedLoads],
  );
  const selectedRowsCount = React.useMemo(
    () => (selection === 'all' ? loads.length : selection.size),
    [selection, loads],
  );

  const emptyPanelProps = React.useMemo(
    () => ({
      title: "It's empty here",
      description: 'No loads found for the selected filters',
    }),
    [],
  );

  return (
    <ListScreenLayout
      filters={<LoadListFilterBar />}
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@LoadListView"
    >
      <DataGrid<LoadRecord>
        columns={columns}
        dataSource={loads}
        fullWidth
        rowKey="id"
        selectionMode="multiple"
        isLoading={loadingState?.isInitialLoading ?? isLoading}
        selectedKeys={selection}
        onSelectionChange={setSelection}
        hasError={isError}
        errorPanelProps={
          error ? { description: error.message, centered: true } : undefined
        }
        emptyPanelProps={emptyPanelProps}
        className="w-full cursor-pointer text-sm"
      />
      <FloatingToolbar visible={selectedRowsCount > 0}>
        <div className="flex items-center gap-2">
          <div className="flex min-w-[145px] items-center justify-end gap-1.5 px-3 text-sm text-neutral/80">
            <FontSemiBold>{selectedRowsCount}</FontSemiBold>
            {selectedRowsCount === 1 ? 'load' : 'loads'} selected
          </div>
          <MarkLoadAsReceivedButton
            loadIds={selectedLoads.map((it) => it.id)}
            variant="outline"
            isDisabled={!canMarkSelectedAsReceived}
            hideOnSuccess={false}
          />
          <PrintDocumentsButton
            originKey="load-list"
            ids={selectedLoads.map((it) => it.id)}
            documents={loadDocuments}
          />
          <DownloadDocumentsButton
            originKey="load-list"
            compressedFileName="loads"
            ids={selectedLoads.map((it) => it.id)}
            documents={loadDocuments}
          />
        </div>
      </FloatingToolbar>
      {loadIdForPrint && (
        <FetchDocumentsPopover
          isOpen
          type="print"
          documents={loadDocuments}
          originKey="load-list-row"
          triggerRef={menuRefs(loadIdForPrint)}
          ids={[loadIdForPrint]}
          onOpenChange={() => setLoadIdForPrint(undefined)}
        />
      )}
      {loadIdForDownload && (
        <FetchDocumentsPopover
          isOpen
          type="download"
          documents={loadDocuments}
          originKey="load-list-row"
          triggerRef={menuRefs(loadIdForDownload)}
          ids={[loadIdForDownload]}
          onOpenChange={() => setLoadIdForDownload(undefined)}
        />
      )}
      {loadIdForReceiving && (
        <MarkLoadAsReceivedPopover
          isOpen
          triggerRef={menuRefs(loadIdForReceiving)}
          loadIds={[loadIdForReceiving]}
          onOpenChange={() => setLoadIdForReceiving(undefined)}
          onSuccess={() => setLoadIdForReceiving(undefined)}
        />
      )}
      {loadIdForReviewing && (
        <MarkLoadAsReviewedPopover
          isOpen
          triggerRef={menuRefs(loadIdForReviewing)}
          loadId={loadIdForReviewing}
          onOpenChange={() => setLoadIdForReviewing(undefined)}
          onSuccess={() => setLoadIdForReviewing(undefined)}
        />
      )}
    </ListScreenLayout>
  );
};
